/**
 * View model for the Exposed Sensitive Documents page.
 *
 * An *exposed sensitive document* is a document that is exposed - public, shared with an
 * external principal, or both - and carries at least one surviving Google Sensitive Data
 * Protection (SDP) detection. The `publish-exposure` step writes one row per document to
 * `organisations/{org}/exposed_sensitive_documents` and one metadata document per provider to
 * `organisations/{org}/exposed_sensitive_documents_metadata`.
 *
 * Every non-rendering decision the page makes lives in a view model, this one or its sibling:
 * here are row and metadata parsing, exposure kind membership, URL state encoding and decoding,
 * which no-rows state the page is in, the info-type category roll-up, counter-card counts,
 * sorting and pagination; the active info-type list and everything that follows from it are in
 * `exposedSensitiveDocuments/activeInfoTypes.ts`, which builds on the categorisation rule here.
 * Nothing in either module may import firebase, a store, `$lib/utils/env` or a `.svelte` file,
 * so both can be probed directly under vitest.
 *
 * Field names are the camelCase Firestore names the publisher writes, i.e. the
 * `CamelCaseBaseModel` aliases of `service/src/redactive/models/exposure_data.py`.
 */
import { validateEmail } from "$lib/utils/email";

import { categoryMeta, customInfoTypeLabel, type NamedCustomInfoType } from "./categoryLabels";
// Type only, so the pair is not a runtime import cycle: `activeInfoTypes` is built on this
// module's categorisation rule, and this module's sort is the only thing that reads back.
import type { TrimmedExposureRow } from "./exposedSensitiveDocuments/activeInfoTypes";
import { parseDateValue } from "./exposedSensitiveDocuments/dateValue";
// `status` reads back only types from here, so the pair is not a runtime import cycle.
import {
  decodeStatusParam,
  statusRankOf,
  type RowStatus,
  type StatusLoadState,
} from "./exposedSensitiveDocuments/status";
import { googleInfoTypes } from "./googleInfoTypes";
import { infoTypes } from "./infoTypes";

/** The two overlapping exposure kinds. A document may be in both. */
export type ExposureKind = "public" | "external";

export const EXPOSURE_KINDS: ExposureKind[] = ["public", "external"];

/** Command-line provider vocabulary, not the `msgraph`/`gdrive` lake storage names. */
export type Provider = "sharepoint" | "google-drive";

export const PROVIDERS: Provider[] = ["sharepoint", "google-drive"];

/** The only statuses a metadata document may carry; `failed` does not exist. */
export type PublishStatus = "published" | "skipped" | "retired";

export const PUBLISH_STATUSES: PublishStatus[] = ["published", "skipped", "retired"];

/**
 * What the exposure filter (counter cards, `?exposure=`) can select. `both` is not a third
 * kind: it selects the documents that are in *both* kinds.
 */
export type ExposureFilter = ExposureKind | "both";

/** One external principal that can reach the document. */
export interface ExposedPrincipal {
  principalResourceId: string;
  principalType?: string;
  principalEmail?: string;
  grantType?: string;
  derivedFrom?: string;
  isWriter: boolean;
  isOwner: boolean;
}

/**
 * One exposed sensitive document.
 *
 * There is no exposure-kind field: membership derives from `isPublic` (public) and a non-empty
 * external principal set (external) - see {@link exposureKindsOf}. `provider` is read off the
 * row and is `undefined` when the row carries a provider this build does not know.
 */
export interface ExposedSensitiveDocument {
  /** Firestore document id, `sha256(resourceId)`. */
  id: string;
  schemaVersion: string;
  orgId: string;
  resourceId: string;
  provider?: Provider;
  publishedRunId: string;

  name: string;
  path: string;
  link: string;
  storageLocation?: string;
  createdAt?: Date;
  modifiedAt?: Date;
  resourceVersion?: string;

  owner?: string;
  ownerEmail?: string;

  isPublic: boolean;
  externalPrincipals: ExposedPrincipal[];
  /** True count, before the publisher's 500-principal cap. */
  externalPrincipalCount: number;
  externalPrincipalsTruncated: boolean;

  /** Raw DLP info-type name -> count of surviving detections. */
  sdpInfoTypeCounts: Record<string, number>;
  sdpTotalFindings: number;
  sensitivityAlgorithm: string;
  /** Keyed by exposure kind; a key is present only for a kind the document is in. */
  findingIds: { public?: string; external?: string };
}

/** Raw preprocess input counts for one provider. */
export interface ExposureInputCounts {
  documents: number;
  documentsWithPermissions: number;
  documentsWithSdpOutput: number;
}

/** Row counts for one exposure kind of one provider. */
export interface ExposureKindCounts {
  /** Rows qualifying before the cap. */
  totalRows: number;
  /** Size of this kind's top-N set, so `truncated = totalRows > publishedRows` holds per kind. */
  publishedRows: number;
  truncated: boolean;
}

/** One `organisations/{org}/exposed_sensitive_documents_metadata/{provider}` document. */
export interface ExposedDocumentsProviderMetadata {
  /** Firestore document id, which is the provider. */
  id: string;
  provider?: Provider;
  status: PublishStatus;
  reason?: string;
  /** Run that last wrote or swept this provider's rows. */
  publishedRunId: string;
  publishedAt?: Date;
  inputCounts: ExposureInputCounts;
  exposureKinds: Record<ExposureKind, ExposureKindCounts>;
}

// ---------------------------------------------------------------------------------------------
// Parsing. Firestore hands back untyped data; every field is defaulted rather than trusted, so a
// row written by an older publisher still renders.
// ---------------------------------------------------------------------------------------------

const parseText = (value: unknown, fallback = ""): string => (typeof value === "string" ? value : fallback);

const parseOptionalText = (value: unknown): string | undefined =>
  typeof value === "string" && value !== "" ? value : undefined;

const parseCount = (value: unknown, fallback = 0): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const parseFlag = (value: unknown, fallback = false): boolean => (typeof value === "boolean" ? value : fallback);

/** `sharepoint` / `google-drive`, or `undefined` for anything this build does not know. */
export function parseProvider(value: unknown): Provider | undefined {
  return value === "sharepoint" || value === "google-drive" ? value : undefined;
}

/**
 * A metadata document's status, or `undefined` for a value this build does not know.
 *
 * It deliberately does not fall back to a status. Defaulting an unknown value to `skipped` would
 * paint an amber "was skipped by the last run" warning and dim live rows on the strength of a
 * string the build cannot read; the caller drops such a document instead - see
 * {@link parseExposureMetadata}.
 */
export function parsePublishStatus(value: unknown): PublishStatus | undefined {
  return PUBLISH_STATUSES.find((status) => status === value);
}

function parseInfoTypeCounts(value: unknown): Record<string, number> {
  if (!value || typeof value !== "object") return {};
  const counts: Record<string, number> = {};
  for (const [infoType, count] of Object.entries(value as Record<string, unknown>)) {
    const parsed = parseCount(count, Number.NaN);
    if (!Number.isNaN(parsed)) counts[infoType] = parsed;
  }
  return counts;
}

function parseExposedPrincipal(value: unknown): ExposedPrincipal {
  const data = (value ?? {}) as Record<string, unknown>;
  return {
    principalResourceId: parseText(data.principalResourceId),
    principalType: parseOptionalText(data.principalType),
    principalEmail: parseOptionalText(data.principalEmail),
    grantType: parseOptionalText(data.grantType),
    derivedFrom: parseOptionalText(data.derivedFrom),
    isWriter: parseFlag(data.isWriter),
    isOwner: parseFlag(data.isOwner),
  };
}

function parseFindingIds(value: unknown): { public?: string; external?: string } {
  if (!value || typeof value !== "object") return {};
  const data = value as Record<string, unknown>;
  const findingIds: { public?: string; external?: string } = {};
  const publicId = parseOptionalText(data.public);
  const externalId = parseOptionalText(data.external);
  if (publicId) findingIds.public = publicId;
  if (externalId) findingIds.external = externalId;
  return findingIds;
}

/** One raw `exposed_sensitive_documents` document -> a row. */
export function parseExposedDocumentRow(id: string, data: unknown): ExposedSensitiveDocument {
  const raw = (data ?? {}) as Record<string, unknown>;
  const externalPrincipals = Array.isArray(raw.externalPrincipals)
    ? (raw.externalPrincipals as unknown[]).map(parseExposedPrincipal)
    : [];
  return {
    id,
    schemaVersion: parseText(raw.schemaVersion, "v1"),
    orgId: parseText(raw.orgId),
    resourceId: parseText(raw.resourceId),
    provider: parseProvider(raw.provider),
    publishedRunId: parseText(raw.publishedRunId),

    name: parseText(raw.name),
    path: parseText(raw.path),
    link: parseText(raw.link),
    storageLocation: parseOptionalText(raw.storageLocation),
    createdAt: parseDateValue(raw.createdAt),
    modifiedAt: parseDateValue(raw.modifiedAt),
    resourceVersion: parseOptionalText(raw.resourceVersion),

    owner: parseOptionalText(raw.owner),
    ownerEmail: parseOptionalText(raw.ownerEmail),

    isPublic: parseFlag(raw.isPublic),
    externalPrincipals,
    externalPrincipalCount: parseCount(raw.externalPrincipalCount, externalPrincipals.length),
    externalPrincipalsTruncated: parseFlag(raw.externalPrincipalsTruncated),

    sdpInfoTypeCounts: parseInfoTypeCounts(raw.sdpInfoTypeCounts),
    sdpTotalFindings: parseCount(raw.sdpTotalFindings),
    sensitivityAlgorithm: parseText(raw.sensitivityAlgorithm, "gcp_sdp"),
    findingIds: parseFindingIds(raw.findingIds),
  };
}

function parseExposureKindCounts(value: unknown): ExposureKindCounts {
  const data = (value ?? {}) as Record<string, unknown>;
  const totalRows = parseCount(data.totalRows);
  const publishedRows = parseCount(data.publishedRows);
  return {
    totalRows,
    publishedRows,
    truncated: parseFlag(data.truncated, totalRows > publishedRows),
  };
}

/**
 * One raw `exposed_sensitive_documents_metadata` document -> per-provider metadata, or
 * `undefined` when its status is one this build does not know.
 *
 * Dropping the document is the honest answer: the page's whole reading of a provider - green or
 * amber, rows live or swept, "has this org been run at all" - hangs off the status, and there is
 * no safe guess. An ignored provider simply has no pill and no warning.
 */
export function parseExposureMetadata(id: string, data: unknown): ExposedDocumentsProviderMetadata | undefined {
  const raw = (data ?? {}) as Record<string, unknown>;
  const status = parsePublishStatus(raw.status);
  if (status === undefined) return undefined;

  const inputCounts = (raw.inputCounts ?? {}) as Record<string, unknown>;
  const exposureKinds = (raw.exposureKinds ?? {}) as Record<string, unknown>;
  return {
    id,
    // The document id *is* the provider, so it is the fallback when the field is missing.
    provider: parseProvider(raw.provider) ?? parseProvider(id),
    status,
    reason: parseOptionalText(raw.reason),
    publishedRunId: parseText(raw.publishedRunId),
    publishedAt: parseDateValue(raw.publishedAt),
    inputCounts: {
      documents: parseCount(inputCounts.documents),
      documentsWithPermissions: parseCount(inputCounts.documentsWithPermissions),
      documentsWithSdpOutput: parseCount(inputCounts.documentsWithSdpOutput),
    },
    exposureKinds: {
      public: parseExposureKindCounts(exposureKinds.public),
      external: parseExposureKindCounts(exposureKinds.external),
    },
  };
}

// ---------------------------------------------------------------------------------------------
// Exposure kind membership
// ---------------------------------------------------------------------------------------------

/** The kinds a row is in: `public` iff `isPublic`, `external` iff it has any external principal. */
export function exposureKindsOf(row: ExposedSensitiveDocument): ExposureKind[] {
  const kinds: ExposureKind[] = [];
  if (row.isPublic) kinds.push("public");
  if (row.externalPrincipalCount > 0 || row.externalPrincipals.length > 0) kinds.push("external");
  return kinds;
}

/** A "both" document: public *and* externally shared. It is counted under all three counters. */
export function isBothDocument(row: ExposedSensitiveDocument): boolean {
  return exposureKindsOf(row).length === 2;
}

/** Membership for the counter cards and `?exposure=`. A null filter matches every row. */
export function rowMatchesExposure(row: ExposedSensitiveDocument, filter: ExposureFilter | null): boolean {
  if (filter === null) return true;
  const kinds = exposureKindsOf(row);
  if (filter === "both") return kinds.length === 2;
  return kinds.includes(filter);
}

/** Rows passing the provider filter; a null filter matches every row. */
export function rowMatchesProvider(row: ExposedSensitiveDocument, filter: Provider | null): boolean {
  return filter === null || row.provider === filter;
}

// ---------------------------------------------------------------------------------------------
// URL state
// ---------------------------------------------------------------------------------------------

/** `?exposure=` -> filter. Absent or unrecognised means "all". */
export function decodeExposureParam(value: string | null | undefined): ExposureFilter | null {
  return value === "public" || value === "external" || value === "both" ? value : null;
}

/** `?provider=` -> filter. Absent or unrecognised means "all". */
export function decodeProviderParam(value: string | null | undefined): Provider | null {
  return parseProvider(value) ?? null;
}

/**
 * Which info-type list the reader has asked for, as the URL says it (spec R10).
 *
 * Three states, not two: the parameter being *absent* is different from it being present and
 * empty. Absent means "whatever the org saved"; present and empty means "I have deliberately
 * cleared it for this session"; non-empty means "this exact list".
 */
export type ActiveInfoTypeSelection =
  | { kind: "org-default" }
  | { kind: "show-all" }
  | { kind: "per-user"; infoTypes: string[] };

/** `?infoTypes` absent: follow whatever the org saved. */
export const FOLLOW_ORG_DEFAULT: ActiveInfoTypeSelection = { kind: "org-default" };
/** `?infoTypes=` present and empty: show every info type, whatever the org saved. */
export const SHOW_ALL_INFO_TYPES: ActiveInfoTypeSelection = { kind: "show-all" };

/**
 * `?infoTypes=` -> the reader's selection.
 *
 * Raw DLP names, comma-separated, in the URL exactly as they are stored and matched - the page
 * never puts a category key in the parameter (spec section 5, "raw DLP info-type names only").
 * Names are trimmed, blanks dropped, and duplicates collapsed keeping the first occurrence, the
 * same normalisation the settings route applies with `dict.fromkeys`.
 *
 * A non-empty parameter that normalises away to nothing (`?infoTypes=,,`) is treated as show-all
 * rather than as an empty per-user list: an empty per-user list would hide every row with no way
 * back except the pill, and show-all is what the reader of that URL can actually see.
 */
export function decodeInfoTypesParam(value: string | null | undefined): ActiveInfoTypeSelection {
  if (value === null || value === undefined) return FOLLOW_ORG_DEFAULT;
  const names = [...new Set(value.split(",").map((name) => name.trim()))].filter((name) => name !== "");
  return names.length === 0 ? SHOW_ALL_INFO_TYPES : { kind: "per-user", infoTypes: names };
}

/**
 * The page's whole URL state: the two row filters, each `null` when it is not applied, plus the
 * info-type selection, which is never `null` because "absent" is one of its three states, and
 * the statuses picked, where the empty list is all statuses (spec 9.5).
 */
export interface ExposureFilters {
  exposure: ExposureFilter | null;
  provider: Provider | null;
  infoTypes: ActiveInfoTypeSelection;
  /** Written to the URL as `?status=`. */
  statuses: RowStatus[];
}

/**
 * Nothing filtered at all - what the filtered-empty state's "Show all" restores. The info-type
 * selection is `all` rather than `org-default`, because an org default that hides every row is
 * one of the ways to reach that state, and returning to it would leave the page still empty.
 */
export const NO_EXPOSURE_FILTERS: ExposureFilters = {
  exposure: null,
  provider: null,
  infoTypes: SHOW_ALL_INFO_TYPES,
  statuses: [],
};

/** A query string (with or without its leading `?`) -> the page's filters. */
export function decodeExposureFilters(querystring: string | null | undefined): ExposureFilters {
  const params = new URLSearchParams(querystring ?? "");
  return {
    exposure: decodeExposureParam(params.get("exposure")),
    provider: decodeProviderParam(params.get("provider")),
    infoTypes: decodeInfoTypesParam(params.get("infoTypes")),
    statuses: decodeStatusParam(params.get("status")),
  };
}

/**
 * The filters -> the query string to push, e.g. `?exposure=both&provider=sharepoint`.
 *
 * A cleared filter drops its parameter rather than writing an empty one, and with everything
 * cleared the result is the empty string - so the caller pushes a bare path and the `?` never
 * survives on its own. `infoTypes` is the exception that proves the rule: show-all is written as
 * the *empty* `?infoTypes=`, because that is how R10 distinguishes it from the absent parameter
 * that means "follow the org default". `status` has no such third state: all statuses writes
 * nothing. The page owns these four parameters
 * and no others, so this builds the whole query string rather than patching the current one.
 *
 * Raw names are joined with commas and percent-encoded by `URLSearchParams`, so a name
 * containing `/` survives; a name containing a literal comma would not, and no DLP name does.
 */
export function encodeExposureFilters(filters: ExposureFilters): string {
  const params = new URLSearchParams();
  if (filters.exposure !== null) params.set("exposure", filters.exposure);
  if (filters.provider !== null) params.set("provider", filters.provider);
  if (filters.infoTypes.kind === "show-all") params.set("infoTypes", "");
  // Raw detector names, never category keys - here and in the saved org default: a category key
  // would re-expand to whatever the category holds today, and could not express a partial
  // category, which is the stale-default signal the chips show.
  if (filters.infoTypes.kind === "per-user") params.set("infoTypes", filters.infoTypes.infoTypes.join(","));
  if (filters.statuses.length > 0) params.set("status", filters.statuses.join(","));
  const query = params.toString();
  return query === "" ? "" : `?${query}`;
}

// ---------------------------------------------------------------------------------------------
// Info-type category roll-up
// ---------------------------------------------------------------------------------------------

/** The explicit bucket for raw info types with no active category on the Sensitive Data page. */
export const UNCATEGORISED_CATEGORY = "uncategorised";
export const UNCATEGORISED_LABEL = "Uncategorised";

export interface InfoTypeCategoryRollUp {
  /** Category key, or {@link UNCATEGORISED_CATEGORY}. */
  category: string;
  label: string;
  /** Summed detection count across the raw info types in this category. */
  count: number;
  /** The category's detectors, count desc then name asc. */
  detectors: InfoTypeDetectorCount[];
  /** Set only on an org custom info type's category, which chips in the custom colour. */
  isCustom?: boolean;
}

/** One detector behind a category chip: its raw DLP name, how it reads, and its count. */
export interface InfoTypeDetectorCount {
  /** The raw DLP name: what is saved and filtered on, never shown. */
  infoType: string;
  /** How the detector reads (#2250): {@link detectorDisplayName}. */
  displayName: string;
  count: number;
}

/**
 * How a detector reads - the one rule the chips, the panel and the category combobox share. An
 * org custom type is its own single detector, so it reads as its category's label. A built-in
 * reads the way Google names it (`IBAN_CODE` -> "Bank IBAN number"), the raw name when Google has none - e.g.
 * a legacy `SWIFT_CODE` - and never as its category label: `categoryMeta`'s overrides
 * disambiguate categories, not detectors.
 */
export function detectorDisplayName(infoType: string, category: Pick<InfoTypeCategory, "label" | "isCustom">): string {
  if (category.isCustom) return category.label;
  return googleInfoTypes[infoType]?.displayName ?? infoType;
}

/** A raw info type's category key and the label that key renders as. */
export interface InfoTypeCategory {
  category: string;
  label: string;
  /** Set only on an org custom info type's category, which chips in the custom colour. */
  isCustom?: boolean;
}

/** The org's enabled custom info types keyed by raw name, the lookup {@link categoryOf} takes. */
export type CustomInfoTypesByName = ReadonlyMap<string, NamedCustomInfoType>;

const NO_CUSTOM_INFO_TYPES: CustomInfoTypesByName = new Map<string, NamedCustomInfoType>();

/** Index the org's enabled custom info types by raw name, once per call rather than per name. */
export function customInfoTypesByName(customInfoTypes: NamedCustomInfoType[] = []): CustomInfoTypesByName {
  return new Map(customInfoTypes.map((customType) => [customType.name, customType]));
}

/**
 * The one categorisation rule on this page: raw DLP name -> category key and label.
 *
 * An enabled custom info type is its own category, keyed by its raw name and labelled by
 * `customInfoTypeLabel`, the Sensitive Data Detection page's own label. Everything else goes
 * through the Sensitive Data Detection page's `infoTypes` map to a category key, and only counts
 * as categorised if that key is one `categoryMeta` shows - a legacy finding such as `SWIFT_CODE`,
 * which the scanner no longer requests and Google gives no parent, lands under {@link
 * UNCATEGORISED_CATEGORY}. There is exactly one fall-through bucket, and both the row chips and
 * the filter bar's chips call this, so the two can never disagree about where a name belongs.
 *
 * Takes a `Map` rather than an array because every caller is inside a loop over raw names.
 */
export function categoryOf(
  infoType: string,
  customInfoTypes: CustomInfoTypesByName = NO_CUSTOM_INFO_TYPES,
): InfoTypeCategory {
  const customType = customInfoTypes.get(infoType);
  if (customType) return { category: infoType, label: customInfoTypeLabel(customType), isCustom: true };
  const mapped = infoTypes[infoType];
  const meta = mapped === undefined ? undefined : categoryMeta[mapped];
  return meta === undefined
    ? { category: UNCATEGORISED_CATEGORY, label: UNCATEGORISED_LABEL }
    : { category: mapped, label: meta.label };
}

/**
 * Categories alphabetical by label, never by count (spec 6.3, the Sensitive Data Detection
 * page's own sort); Uncategorised always last.
 */
export function byLabelUncategorisedLast(a: InfoTypeCategory, b: InfoTypeCategory): number {
  if (a.category === UNCATEGORISED_CATEGORY) return b.category === UNCATEGORISED_CATEGORY ? 0 : 1;
  if (b.category === UNCATEGORISED_CATEGORY) return -1;
  return a.label.localeCompare(b.label);
}

export interface RollUpInfoTypesOptions {
  /**
   * The org's *enabled* custom info types (from the management API, not Firestore). Each is its
   * own category.
   */
  customInfoTypes?: NamedCustomInfoType[];
}

/**
 * Roll raw DLP info-type counts up to the Sensitive Data Detection page's categories - the one
 * roll-up behind the table's Categories chips and the side panel's Categories section: per
 * category its summed count and its detectors, each with its display name and own count.
 *
 * The mapping is that page's own `infoTypes` (raw name -> category key) and `categoryMeta`
 * (category key -> label). A raw type with no mapping, or one mapping to a category that page
 * no longer shows, goes under {@link UNCATEGORISED_CATEGORY} - e.g. a legacy `SWIFT_CODE` finding,
 * which the scanner no longer requests and Google gives no parent.
 */
export function rollUpInfoTypes(
  sdpInfoTypeCounts: Record<string, number> | undefined,
  options: RollUpInfoTypesOptions = {},
): InfoTypeCategoryRollUp[] {
  const customTypes = customInfoTypesByName(options.customInfoTypes);
  const byCategory = new Map<string, InfoTypeCategoryRollUp>();

  for (const [infoType, count] of Object.entries(sdpInfoTypeCounts ?? {})) {
    const category = categoryOf(infoType, customTypes);
    const detector = { infoType, displayName: detectorDisplayName(infoType, category), count };

    const existing = byCategory.get(category.category);
    if (existing) {
      existing.count += count;
      existing.detectors.push(detector);
    } else {
      byCategory.set(category.category, { ...category, count, detectors: [detector] });
    }
  }

  for (const rollUp of byCategory.values()) {
    rollUp.detectors.sort((a, b) => b.count - a.count || a.infoType.localeCompare(b.infoType));
  }

  return [...byCategory.values()].sort(byLabelUncategorisedLast);
}

// ---------------------------------------------------------------------------------------------
// Row detail: external principals (#2205)
// ---------------------------------------------------------------------------------------------

/**
 * The publisher's own sort key for `externalPrincipals` (design 3.1, decided at #2190 Q4):
 * `isOwner` desc, `isWriter` desc, `principalEmail` asc with nulls last, `principalResourceId`
 * asc. Total and deterministic, so re-sorting a row the current publisher wrote is a no-op; it
 * only reorders rows an older publisher left unsorted.
 */
export function compareExposedPrincipals(a: ExposedPrincipal, b: ExposedPrincipal): number {
  if (a.isOwner !== b.isOwner) return a.isOwner ? -1 : 1;
  if (a.isWriter !== b.isWriter) return a.isWriter ? -1 : 1;
  const aEmail = a.principalEmail;
  const bEmail = b.principalEmail;
  if (aEmail !== bEmail) {
    // A principal with no email sorts after every principal that has one, in the same rank group.
    if (!aEmail) return 1;
    if (!bEmail) return -1;
    const byEmail = aEmail.localeCompare(bEmail);
    if (byEmail !== 0) return byEmail;
  }
  return a.principalResourceId.localeCompare(b.principalResourceId);
}

/** Non-mutating wrapper around {@link compareExposedPrincipals}. */
export function sortExposedPrincipals(principals: ExposedPrincipal[]): ExposedPrincipal[] {
  return [...principals].sort(compareExposedPrincipals);
}

/** The publisher's per-row cap on `externalPrincipals` (design 3.1); the panel restates it. */
export const EXTERNAL_PRINCIPAL_CAP = 500;

export interface ExternalPrincipalsView {
  /** Sorted, then capped at {@link EXTERNAL_PRINCIPAL_CAP}: exactly what the panel lists. */
  shown: ExposedPrincipal[];
  /** Distinct external principals on the document, before any cap. */
  trueCount: number;
  /** True when `shown` is not the whole set - capped by the publisher, by the cap here, or both. */
  truncated: boolean;
}

/**
 * What the panel shows for external principals: the sorted, capped list plus the true count the
 * truncation notice quotes. `externalPrincipalCount` is the count before the publisher's cap, so
 * it, not `shown.length`, is the number of people with access.
 */
export function externalPrincipalsView(
  row: ExposedSensitiveDocument,
  cap: number = EXTERNAL_PRINCIPAL_CAP,
): ExternalPrincipalsView {
  const sorted = sortExposedPrincipals(row.externalPrincipals);
  const shown = sorted.length > cap ? sorted.slice(0, cap) : sorted;
  const trueCount = Math.max(row.externalPrincipalCount, sorted.length);
  return { shown, trueCount, truncated: row.externalPrincipalsTruncated || shown.length < trueCount };
}

// ---------------------------------------------------------------------------------------------
// Row detail: info types (#2205)
// ---------------------------------------------------------------------------------------------

export interface PanelInfoTypeOptions extends RollUpInfoTypesOptions {
  /**
   * The org's active info-type list (#2206). When given, raw types outside it are dropped before
   * the roll-up; when omitted the whole count map is grouped, which is the show-all behaviour.
   */
  activeInfoTypes?: Iterable<string>;
}

/**
 * The row's `sdpInfoTypeCounts` grouped for the side panel: the Sensitive Data Detection page's
 * categories, alphabetical by label with Uncategorised last, each with its raw breakdown - i.e.
 * {@link rollUpInfoTypes}, optionally trimmed to the active list first (design 6.4).
 */
export function panelInfoTypeGroups(
  row: ExposedSensitiveDocument,
  options: PanelInfoTypeOptions = {},
): InfoTypeCategoryRollUp[] {
  const { activeInfoTypes, ...rollUpOptions } = options;
  const counts = row.sdpInfoTypeCounts ?? {};
  if (activeInfoTypes === undefined) return rollUpInfoTypes(counts, rollUpOptions);
  const active = new Set(activeInfoTypes);
  const trimmed = Object.fromEntries(Object.entries(counts).filter(([infoType]) => active.has(infoType)));
  return rollUpInfoTypes(trimmed, rollUpOptions);
}

/**
 * The "N findings total" in the panel's Info types heading (design 6.4).
 *
 * The same rule the table's Findings cell follows (`trimRowsToInfoTypes`, spec 6.2): under
 * show-all it is the publisher's own `sdpTotalFindings`, shown verbatim because nothing is
 * trimmed; under an active list it is the **sum of the groups on screen**. Reading
 * `sdpTotalFindings` in both cases is what makes a filtered panel claim a total the list beneath
 * it - and the row the reviewer clicked - do not add up to.
 */
export function panelFindingsTotal(
  row: ExposedSensitiveDocument,
  groups: InfoTypeCategoryRollUp[],
  activeInfoTypes?: Iterable<string> | null,
): number {
  if (activeInfoTypes === undefined || activeInfoTypes === null) return row.sdpTotalFindings;
  return groups.reduce((total, group) => total + group.count, 0);
}

// ---------------------------------------------------------------------------------------------
// Row detail: contact the document author (#2223)
// ---------------------------------------------------------------------------------------------

/**
 * The panel's "Contact document author" action (design 6.4, as amended by #2222): a `mailto:` to
 * the row's `ownerEmail`, the same action the Permissions Assurance finding flyover offers.
 *
 * The three states the panel renders: `unknown` (no owner email published: "owner unknown", contact
 * disabled), `invalid` (an `ownerEmail` that fails {@link validateEmail}: shown as text, contact
 * disabled with a tooltip) and `valid` (shown, contact is the `href`). Never hidden, never a dead
 * link. `email` is the trimmed value as published.
 */
export type OwnerContact =
  | { state: "unknown" }
  | { state: "invalid"; email: string }
  | { state: "valid"; email: string; href: string };

export function ownerContactOf(row: Pick<ExposedSensitiveDocument, "name" | "ownerEmail">): OwnerContact {
  const email = row.ownerEmail?.trim();
  if (!email) return { state: "unknown" };
  if (!validateEmail(email)) return { state: "invalid", email };
  // validateEmail admits `? # & % / =` in the local part, so `a?cc=x@evil.com` would inject a
  // header: percent-encode the local part. The domain is alphanumerics, `-` and `.` only.
  const at = email.indexOf("@");
  const address = `${encodeURIComponent(email.slice(0, at))}@${email.slice(at + 1)}`;
  // The flyover's subject wording, but encoded: a document name with `&`, `#` or `?` in it must
  // not cut the subject short or leak into another mailto header.
  const subject = encodeURIComponent(`Issue detected on ${row.name}`);
  return { state: "valid", email, href: `mailto:${address}?subject=${subject}` };
}

// ---------------------------------------------------------------------------------------------
// Counter cards
// ---------------------------------------------------------------------------------------------

export interface ExposureKindTally {
  public: number;
  external: number;
  both: number;
}

/**
 * Counter-card counts over the rows already passing the other filters. A both-document counts
 * under all three cards, so the three numbers deliberately do not sum to the row count.
 */
export function countByExposureKind(rows: ExposedSensitiveDocument[]): ExposureKindTally {
  const tally: ExposureKindTally = { public: 0, external: 0, both: 0 };
  for (const row of rows) {
    const kinds = exposureKindsOf(row);
    if (kinds.includes("public")) tally.public += 1;
    if (kinds.includes("external")) tally.external += 1;
    if (kinds.length === 2) tally.both += 1;
  }
  return tally;
}

// ---------------------------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------------------------

export type SortColumn = "findings" | "document" | "status";
export type SortDirection = "asc" | "desc";

export interface RowSort {
  column: SortColumn;
  direction: SortDirection;
}

export const DEFAULT_SORT: RowSort = { column: "findings", direction: "desc" };

/** What a sort compares on, whatever the row is wrapped in. */
interface SortKeys {
  findings: number;
  name: string;
  /** Place in the attention rank; only the Status sort reads it. */
  statusRank: number;
  resourceId: string;
}

type CompareKeys = (left: SortKeys, right: SortKeys) => number;

/** Each column's primary comparison, ascending; the sort direction flips it. */
const SORT_COLUMNS: Record<SortColumn, CompareKeys> = {
  findings: (left, right) => left.findings - right.findings,
  document: (left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: "base" }),
  status: (left, right) => left.statusRank - right.statusRank,
};

/**
 * A column's own tie-break, applied before `resourceId` and **not** flipped by the direction.
 * Status ties fall to Findings desc first, whichever way Status runs (spec 9.5).
 */
const SORT_TIE_BREAKS: Partial<Record<SortColumn, CompareKeys>> = {
  status: (left, right) => right.findings - left.findings,
};

function sortByKeys<T>(rows: T[], sort: RowSort, keysOf: (row: T) => SortKeys): T[] {
  const sign = sort.direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const left = keysOf(a);
    const right = keysOf(b);
    const primary = SORT_COLUMNS[sort.column](left, right);
    if (primary !== 0) return sign * primary;
    const tieBreak = SORT_TIE_BREAKS[sort.column]?.(left, right) ?? 0;
    if (tieBreak !== 0) return tieBreak;
    return left.resourceId.localeCompare(right.resourceId);
  });
}

/**
 * The sort the page actually applies. On a failed status read there is no rank to sort on (spec
 * 9.3, a row is never ranked as `open` on a read that did not land), so the Status header is not
 * sortable and a Status sort already in place falls back to {@link DEFAULT_SORT}.
 */
export function effectiveSort(sort: RowSort, statusState: StatusLoadState): RowSort {
  return sort.column === "status" && statusState === "failed" ? DEFAULT_SORT : sort;
}

/**
 * Client-side sort over the fully loaded set, on the **trimmed** Findings total - so the Findings
 * column is always sorted on the number it is displaying (spec 6.2, "sort follows the same
 * value"), and never on the publisher's untrimmed `sdpTotalFindings`. Status sorts on the
 * attention rank (asc = `reopened` first), its ties falling to Findings desc. Ties then always
 * break on `resourceId` ascending, in both directions, so the order is total and a re-sort never
 * reshuffles equal rows. The page has no second sort: every row reaching the table is a
 * {@link TrimmedExposureRow}, show-all included.
 */
export function sortTrimmedRows(rows: TrimmedExposureRow[], sort: RowSort = DEFAULT_SORT): TrimmedExposureRow[] {
  return sortByKeys(rows, sort, (trimmed) => ({
    findings: trimmed.findings,
    name: trimmed.row.name,
    statusRank: statusRankOf(trimmed.row),
    resourceId: trimmed.row.resourceId,
  }));
}

// ---------------------------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------------------------

export const DEFAULT_PAGE_SIZE = 25;

export interface PageOfRows<T> {
  items: T[];
  /** 1-based index of the first item on the page, 0 when there are none. */
  start: number;
  /** 1-based index of the last item on the page, 0 when there are none. */
  end: number;
  total: number;
  pageCount: number;
}

/** One 1-based page of rows. `page` is clamped, so an out-of-range page shows the nearest one. */
export function paginate<T>(rows: T[], page: number, pageSize: number = DEFAULT_PAGE_SIZE): PageOfRows<T> {
  const total = rows.length;
  const pageCount = Math.ceil(total / pageSize);
  if (total === 0) return { items: [], start: 0, end: 0, total: 0, pageCount: 0 };

  const current = Math.min(Math.max(Math.trunc(page) || 1, 1), pageCount);
  const start = (current - 1) * pageSize;
  const items = rows.slice(start, start + pageSize);
  return { items, start: start + 1, end: start + items.length, total, pageCount };
}

/** The table footer label, e.g. `1-25 of 80 items`. */
export function pageSummary(page: PageOfRows<unknown>): string {
  if (page.total === 0) return "0 items";
  return `${page.start}–${page.end} of ${page.total} items`;
}

/** The word that marks a count as only the rows loaded so far, while the rows still load (#2265). */
export const PROVISIONAL_LABEL = "so far";

/** The suffix a provisional count carries after its number, e.g. `12 so far`; empty once loaded. */
export function provisionalSuffix(provisional: boolean): string {
  return provisional ? ` ${PROVISIONAL_LABEL}` : "";
}

// ---------------------------------------------------------------------------------------------
// Page state: which providers are live, which rows survive, which no-rows state the page is in
// ---------------------------------------------------------------------------------------------

/** Providers whose metadata carries `status`. A document with no readable provider is skipped. */
export function providersWithStatus(metadata: ExposedDocumentsProviderMetadata[], status: PublishStatus): Provider[] {
  return metadata.flatMap((meta) => (meta.status === status && meta.provider ? [meta.provider] : []));
}

/**
 * When `provider` was last published, only while its metadata reads `published` (D2 of the
 * reopened line). No provider, or one that is skipped, retired or absent, has no publish time.
 */
export function publishedAtOf(
  metadata: ExposedDocumentsProviderMetadata[],
  provider: Provider | null | undefined,
): Date | undefined {
  if (!provider) return undefined;
  return metadata.find((meta) => meta.provider === provider && meta.status === "published")?.publishedAt;
}

/**
 * Whether the pipeline has actually run for this org, i.e. at least one provider reads
 * `published` or `skipped` (spec 6.1, the sidebar's visibility rule).
 *
 * `retired` does not count: it records that a provider's rows were *swept*, so an org whose only
 * metadata is retired has nothing to show and is back in the off state, not in "the last run
 * found nothing exposed". The page and the sidebar entry ask this same question, so a retired-
 * only org loses its sidebar entry and renders the off panel rather than a false all-clear.
 */
export function hasActiveMetadata(metadata: ExposedDocumentsProviderMetadata[]): boolean {
  return metadata.some((meta) => meta.status === "published" || meta.status === "skipped");
}

/**
 * Rows a retired provider's sweep should have removed.
 *
 * A retired provider's rows were deleted by the run; any that survived a failed sweep are stale
 * by definition, so they are dropped here rather than shown. A row whose provider this build
 * cannot read is kept - it cannot be attributed to a retired provider.
 */
export function dropRetiredRows(
  rows: ExposedSensitiveDocument[],
  metadata: ExposedDocumentsProviderMetadata[],
): ExposedSensitiveDocument[] {
  const retired = providersWithStatus(metadata, "retired");
  if (retired.length === 0) return rows;
  return rows.filter((row) => row.provider === undefined || !retired.includes(row.provider));
}

/** The metadata documents the provider filter admits; a null filter admits every one. */
export function metadataForProvider(
  metadata: ExposedDocumentsProviderMetadata[],
  filter: Provider | null,
): ExposedDocumentsProviderMetadata[] {
  return filter === null ? metadata : metadata.filter((meta) => meta.provider === filter);
}

/**
 * The skipped providers with at least one row among `rowsOnScreen`.
 *
 * A skipped provider's rows are never hidden, only dimmed, so the warning that explains the
 * dimming is owed exactly when one of its rows is visible - not merely when the provider was
 * skipped.
 */
export function skippedMetadataOnScreen(
  metadata: ExposedDocumentsProviderMetadata[],
  rowsOnScreen: ExposedSensitiveDocument[],
): ExposedDocumentsProviderMetadata[] {
  return metadata.filter(
    (meta) => meta.status === "skipped" && rowsOnScreen.some((row) => row.provider === meta.provider),
  );
}

/** Which of the three no-rows states the page is in (spec 6.2). */
export type EmptyStateVariant = "off" | "empty" | "filtered-empty";

export interface PageStateInput {
  metadata: ExposedDocumentsProviderMetadata[];
  /** Rows left after the retired sweep, before the exposure and provider filters. */
  liveRowCount: number;
  /** Rows left after those filters. */
  filteredRowCount: number;
}

/**
 * The no-rows state to draw, or `null` when there are rows and the table draws instead.
 *
 *  - `off`             nothing has been published or skipped: the two pipeline steps have not
 *                      run for this org (or their output has since been retired), so there is
 *                      nothing to be empty *of*.
 *  - `empty`           a run published and found nothing exposed.
 *  - `filtered-empty`  rows exist, but the filters hide all of them.
 */
export function emptyStateVariant({
  metadata,
  liveRowCount,
  filteredRowCount,
}: PageStateInput): EmptyStateVariant | null {
  if (!hasActiveMetadata(metadata)) return "off";
  if (liveRowCount === 0) return "empty";
  return filteredRowCount === 0 ? "filtered-empty" : null;
}

// ---------------------------------------------------------------------------------------------
// Freshness and truncation
// ---------------------------------------------------------------------------------------------

export const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/** Whole days between `date` and `now`, floored, never negative. */
export function daysAgo(date: Date | undefined, now: Date = new Date()): number | undefined {
  if (!date) return undefined;
  return Math.max(0, Math.floor((now.getTime() - date.getTime()) / MILLISECONDS_PER_DAY));
}

const NO_REASON_RECORDED = "no reason recorded";

interface FreshnessBase {
  provider?: Provider;
  providerLabel: string;
}

/**
 * A provider's publish state, as the skipped-provider warning reads it. There is no age threshold: a published provider
 * is green however old it is, and the page shows the absolute date plus `Nd ago` beside it.
 */
export type ExposureFreshness =
  | (FreshnessBase & {
      status: "published";
      publishedAt?: Date;
      daysAgo?: number;
      /** `3d ago`, or undefined when the metadata carries no publish time. */
      ageLabel?: string;
    })
  | (FreshnessBase & { status: "skipped"; reason: string })
  | (FreshnessBase & { status: "retired"; reason: string });

export function describeFreshness(meta: ExposedDocumentsProviderMetadata, now: Date = new Date()): ExposureFreshness {
  const base: FreshnessBase = { provider: meta.provider, providerLabel: providerLabel(meta.provider ?? meta.id) };
  if (meta.status === "published") {
    const age = daysAgo(meta.publishedAt, now);
    return {
      ...base,
      status: "published",
      publishedAt: meta.publishedAt,
      daysAgo: age,
      ageLabel: age === undefined ? undefined : `${age}d ago`,
    };
  }
  return { ...base, status: meta.status, reason: meta.reason ?? NO_REASON_RECORDED };
}

export interface ExposureTruncationNotice {
  kind: ExposureKind;
  kindLabel: string;
  publishedRows: number;
  totalRows: number;
  /** `top 500 of 1200`. */
  label: string;
}

/** One notice per truncated exposure kind of a provider; empty when nothing was capped. */
export function truncationNotice(meta: ExposedDocumentsProviderMetadata): ExposureTruncationNotice[] {
  return EXPOSURE_KINDS.filter((kind) => meta.exposureKinds[kind]?.truncated).map((kind) => {
    const counts = meta.exposureKinds[kind];
    return {
      kind,
      kindLabel: exposureKindLabel(kind),
      publishedRows: counts.publishedRows,
      totalRows: counts.totalRows,
      label: `top ${counts.publishedRows} of ${counts.totalRows}`,
    };
  });
}

// ---------------------------------------------------------------------------------------------
// Display labels
// ---------------------------------------------------------------------------------------------

const PROVIDER_LABELS: Record<Provider, string> = {
  sharepoint: "SharePoint",
  "google-drive": "Google Drive",
};

export function providerLabel(provider: string | undefined): string {
  if (!provider) return "Unknown provider";
  return PROVIDER_LABELS[provider as Provider] ?? provider;
}

const EXPOSURE_FILTER_LABELS: Record<ExposureFilter, string> = {
  public: "Public",
  external: "Externally shared",
  both: "Public + External",
};

export function exposureKindLabel(kind: ExposureKind): string {
  return EXPOSURE_FILTER_LABELS[kind];
}

export function exposureFilterLabel(filter: ExposureFilter): string {
  return EXPOSURE_FILTER_LABELS[filter];
}
